import { Archive, Download, File as FileIcon, Link2 } from "lucide-react";
import { Badge, Button } from "@/design-system/components/ui";
import { DataTable, type Column } from "@/components/layout/DataTable";
import { documentsApi } from "@/api/documents";
import type { DocumentDto } from "@/types/api";
import { useDateFormatter } from "@/utils/datetime";

interface DocumentListProps {
  documents: DocumentDto[];
  loading?: boolean;
  error?: string | null;
  selectedIds: string[];
  onSelectionChange: (next: string[]) => void;
  onSelectDocument?: (doc: DocumentDto) => void;
}

const isArchived = (d: DocumentDto) => Boolean(d.isArchived ?? d.archivedAt);

export function DocumentList({
  documents,
  loading,
  error,
  selectedIds,
  onSelectionChange,
  onSelectDocument,
}: DocumentListProps) {
  const { date } = useDateFormatter();
  const columns: Column<DocumentDto>[] = [
    {
      key: "title",
      header: "Document",
      render: (d) => (
        <button
          type="button"
          className="flex items-start gap-space-2 text-left hover:underline"
          onClick={() => onSelectDocument?.(d)}
        >
          <FileIcon size={14} aria-hidden className="mt-[2px] text-gold shrink-0" />
          <div>
            <div
              className={`font-semibold ${isArchived(d) ? "text-gray-mid" : "text-onyx"}`}
            >
              {d.title}
              {(d.linkedCount ?? 0) > 0 && (
                <span
                  title={`Linked to ${d.linkedCount} document${d.linkedCount === 1 ? "" : "s"}`}
                  className="ml-space-2 inline-flex align-middle text-gray-mid"
                >
                  <Link2 size={12} aria-hidden />
                </span>
              )}
              {isArchived(d) && (
                <span className="ml-space-2 inline-block">
                  <ArchivedPill />
                </span>
              )}
            </div>
            <div className="text-caption text-gray-mid">{d.fileName}</div>
          </div>
        </button>
      ),
    },
    {
      key: "category",
      header: "Category",
      render: (d) => <Badge status="info">{d.category}</Badge>,
    },
    { key: "version", header: "Ver.", render: (d) => `v${d.version}` },
    { key: "size", header: "Size", render: (d) => formatBytes(d.fileSizeBytes) },
    {
      key: "ingestion",
      header: "Ingestion",
      render: (d) => <IngestionBadge doc={d} />,
    },
    {
      key: "uploaded",
      header: "Uploaded",
      render: (d) => date(d.createdAt),
    },
    {
      key: "actions",
      header: "",
      render: (d) => (
        <Button
          variant="tertiary"
          size="sm"
          onClick={async () => {
            const res = await documentsApi.download(d.id);
            window.open(res.sasUrl, "_blank", "noopener");
          }}
        >
          <Download size={14} aria-hidden />
          Download
        </Button>
      ),
    },
  ];

  return (
    <DataTable
      columns={columns}
      rows={documents}
      loading={loading}
      error={error ?? null}
      emptyMessage="No documents in this folder."
      rowKey={(d) => d.id}
      selectedIds={selectedIds}
      onSelectionChange={onSelectionChange}
    />
  );
}

function ArchivedPill() {
  return (
    <span
      className="inline-flex items-center gap-space-1 px-space-2 py-[1px] rounded-full text-caption font-display font-semibold uppercase tracking-label align-middle border"
      // Muted gold/amber per Document Archive v1.0 design spec — strong
      // enough that an archived row in a mixed list is unmistakable, but
      // still subordinate to active-row styling.
      style={{
        backgroundColor: "rgba(201, 169, 97, 0.20)",
        borderColor: "rgba(168, 136, 75, 0.50)",
        color: "#8B6F2E",
      }}
    >
      <Archive size={10} aria-hidden /> Archived
    </span>
  );
}

function IngestionBadge({ doc }: { doc: DocumentDto }) {
  // When the backend flags a failure, we've also captured the error
  // message on the row — surface it on hover so the operator can
  // diagnose without opening a separate detail panel.
  const title = doc.ingestionErrorMessage ?? undefined;
  switch (doc.ingestionStatus) {
    case "Completed":
    case "Ingested":
      return <Badge status="finalized">Ready</Badge>;
    case "Failed":
      return (
        <span title={title}>
          <Badge status="review">Failed</Badge>
        </span>
      );
    case "NotIngested":
      return <Badge status="draft">Not ingested</Badge>;
    case "Pending":
      return <Badge status="draft">Processing</Badge>;
    default:
      return <Badge status="draft">{doc.ingestionStatus}</Badge>;
  }
}

function formatBytes(bytes: number) {
  if (bytes < 1024) return `${bytes} B`;
  if (bytes < 1024 * 1024) return `${(bytes / 1024).toFixed(0)} KB`;
  return `${(bytes / 1024 / 1024).toFixed(1)} MB`;
}
